import React from 'react';
import { Paper, Box, Typography, Chip, Tooltip } from '@mui/material';
import { 
  FaRegFilePdf, 
  FaRegFilePowerpoint, 
  FaRegFileVideo,
  FaRegFile 
} from 'react-icons/fa';
import { 
  PiMicrosoftWordLogo, 
  PiMicrosoftExcelLogo 
} from 'react-icons/pi';
import { LuFileText } from 'react-icons/lu';

const ResourceTile = ({ document, onClick }) => {
  // Get appropriate icon based on file type
  const getFileIcon = (fileType) => {
    const iconProps = { size: 48, color: '#1976d2' };
    
    switch (fileType) {
      case 'pdf':
        return <FaRegFilePdf {...iconProps} style={{ color: '#d32f2f' }} />;
      case 'word':
        return <PiMicrosoftWordLogo {...iconProps} style={{ color: '#2e7d32' }} />;
      case 'excel':
        return <PiMicrosoftExcelLogo {...iconProps} style={{ color: '#388e3c' }} />;
      case 'powerpoint':
        return <FaRegFilePowerpoint {...iconProps} style={{ color: '#f57c00' }} />;
      case 'text':
        return <LuFileText {...iconProps} style={{ color: '#616161' }} />;
      case 'video':
        return <FaRegFileVideo {...iconProps} style={{ color: '#7b1fa2' }} />;
      default:
        return <FaRegFile {...iconProps} style={{ color: '#616161' }} />;
    }
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      'Benefits': 'success',
      'Policies': 'error',
      'Presentations': 'warning',
      'Forms': 'info',
      'Uncategorized': 'default'
    };
    return colors[category] || 'default';
  };

  // Format file size for display
  const formatFileSize = (size) => {
    if (!size) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format last modified date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <Paper
      sx={{
        p: 2,
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1.5,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: 'action.hover',
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
        minHeight: 200,
        position: 'relative'
      }}
      onClick={onClick}
    >
      {/* Category Chip */}
      <Chip
        label={document.category}
        size="small"
        color={['Benefits', 'Policies'].includes(document.category) ? undefined : getCategoryColor(document.category)}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          fontSize: '0.7rem',
          height: 20,
          ...(document.category === 'Benefits' && {
            backgroundColor: '#81c784', // Lighter green
            color: '#000000', // Black text
            '&:hover': {
              backgroundColor: '#66bb6a'
            }
          }),
          ...(document.category === 'Policies' && {
            backgroundColor: '#00acc1', // Teal background
            color: '#ffffff', // White text
            '&:hover': {
              backgroundColor: '#0097a7'
            }
          })
        }}
      />

      {/* File Icon */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 60
        }}
      >
        {getFileIcon(document.fileType)}
      </Box>

      {/* Document Name */}
      <Tooltip title={document.displayName} arrow>
        <Typography 
          variant="body1" 
          sx={{ 
            fontWeight: 500,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '2.4em',
            flex: 1,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {document.displayName}
        </Typography>
      </Tooltip>

      {/* File Info */}
      <Box sx={{ mt: 'auto', width: '100%' }}>
        {document.description && (
          <Tooltip title={document.description} arrow>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{
                fontSize: '0.75rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: 0.5
              }}
            >
              {document.description}
            </Typography>
          </Tooltip>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(document.size)}
          </Typography>
          {document.lastModified && (
            <Typography variant="caption" color="text.secondary">
              {formatDate(document.lastModified)}
            </Typography>
          )}
        </Box>

        {/* Configuration Status */}
        {!document.configured && (
          <Typography 
            variant="caption" 
            color="warning.main"
            sx={{ 
              fontSize: '0.7rem',
              fontStyle: 'italic',
              mt: 0.5,
              display: 'block'
            }}
          >
            Auto-detected
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default ResourceTile;
